import React, { FC, useContext } from 'react'
import {
    StyledHint,
    StyledManageAccount,
    StyledOptionItem,
} from './ManageAccount.styled'
import { StyledIconChevron } from '../MainScreen/MainScreen.styled'
import { useSelector } from 'react-redux'
import { AppState, Product } from '@news-mono/web-common'
import { UserSettingsLink } from '../components/UserSettingsLink'
import { tokens } from '@news-mono/design-tokens'
import { MyAccountContext } from '../hooks/useMyAccountContext'
import { useProduct } from '../../__product'
import { calcRem } from '../../__styling'
import { useDebugQueryParams } from '../hooks/useDebugQueryParams'

// TheNightly colors
const tnColors = tokens.thenightly.colors
const tnPalette = tnColors.palette

// PerthNow colors
const pnColors = tokens.perthnow.colors
const pnPalette = pnColors.palette

interface OptionItemProps {
    label: string
    text: string
    // show greyed-out edit option if disabled
    disabled?: boolean
    // hide edit option if not editable
    editable?: boolean
    hint?: JSX.Element
    onClick: () => void
}
export const OptionItem: FC<OptionItemProps> = ({
    label,
    text,
    hint,
    disabled,
    editable = true,
    onClick,
}) => {
    return (
        <StyledOptionItem
            disabled={disabled}
            onClick={onClick}
            editable={editable}
            tabIndex={0}
            onKeyDown={(event) => {
                if (event.key === 'Enter') {
                    onClick()
                }
            }}
        >
            <div>
                <div className="value-wrapper">
                    <small>{label}</small>
                    <p>{text}</p>
                </div>
                {editable && (
                    <span>
                        Edit
                        <StyledIconChevron />
                    </span>
                )}
            </div>
            {hint}
        </StyledOptionItem>
    )
}
export const ManageAccount = () => {
    const authState = useSelector((state: AppState) => state.authentication)
    const { userName, userEmail, loginProvider, emailVerified } = authState
    const { setScreen } = useContext(MyAccountContext)
    const product = useProduct()
    const debugParams = useDebugQueryParams()

    const isPn = product === Product.PerthNow

    // Allow it to consume debugParams as a forefront check
    let isSocialLogin = ['Google', 'Apple'].includes(loginProvider)
    let isEmailVerified = emailVerified

    if (debugParams.forceInternal && isSocialLogin) {
        isSocialLogin = false
        console.log('ManageAccount consuming debug forceInternal')
    }
    if (debugParams.emailVerified !== null) {
        isEmailVerified = debugParams.emailVerified
        console.log('ManageAccount consuming debug emailVerified')
    }

    const socialLink = isSocialLogin && (
        <a
            href={
                loginProvider === 'Apple'
                    ? `https://appleid.apple.com`
                    : `https://myaccount.google.com`
            }
            target="_blank"
        >
            {loginProvider}
        </a>
    )

    const verifyEmailHint = !isEmailVerified ? (
        <StyledHint>Verify your email to edit.</StyledHint>
    ) : undefined
    const emailHint = isSocialLogin ? (
        <StyledHint>
            You can review and manage your account in {socialLink}
        </StyledHint>
    ) : !isEmailVerified ? (
        verifyEmailHint
    ) : undefined

    return (
        <StyledManageAccount>
            <ul>
                <OptionItem
                    label="Name"
                    text={userName}
                    onClick={() => !isSocialLogin && setScreen('change-name')}
                    editable={!isSocialLogin}
                />
                <OptionItem
                    label="Email"
                    text={userEmail!}
                    hint={emailHint}
                    onClick={() =>
                        !isSocialLogin &&
                        isEmailVerified &&
                        setScreen('change-email')
                    }
                    disabled={!isEmailVerified}
                    editable={!isSocialLogin}
                />
                {!isSocialLogin && (
                    <OptionItem
                        label="Password"
                        text={isPn ? '*************' : '********'}
                        disabled={!isEmailVerified}
                        hint={verifyEmailHint}
                        onClick={() =>
                            isEmailVerified && setScreen('change-password')
                        }
                    />
                )}
            </ul>
            <UserSettingsLink
                color={
                    isPn ? pnPalette.greyCoal : tnPalette.utility.error.default
                }
                margin={isPn ? calcRem(16, 0) : undefined}
                display={isPn ? 'block' : undefined}
                onClick={() => setScreen('delete-account')}
            >
                Delete account
            </UserSettingsLink>
        </StyledManageAccount>
    )
}
